import { message } from "antd";
import { useCallback, useRef } from "react";

import type { SpecificationUnpublishedInfo } from "@btnet/services-btnet/handlers/models/specificationUnpublishedInfo";
import {
    useSpecificationsSaveToCatalogByIdPostV2,
    useSpecificationsSaveToCatalogPostV2,
    useSpecificationsUnpublishedChangesInfoPostV2,
} from "@btnet/services-btnet/handlers/specifications/specifications";

import { type EntityIdJobIdResponse } from "types/apiResponse/apiResponse";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { btConfirmAsync } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { useFormatDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";

/**
 * Pure: "Save to catalog" action visibility. Only for builders with add permission on a regular
 * job-level spec when the Global Spec Catalog flag is on — never for general-job (catalog) specs,
 * since a catalog spec cannot be saved back to itself. Shared by the Book View viewer, the AG Grid
 * detail modal, and the grid row-actions menu so all three surfaces gate identically.
 */
export function canShowSaveToCatalogButton({
    isBuilder,
    canAdd,
    actionsVisible,
    isGeneralJob,
    isGlobalSpecCatalogEnabled,
}: {
    isBuilder: boolean;
    canAdd: boolean;
    actionsVisible: boolean;
    isGeneralJob: boolean;
    isGlobalSpecCatalogEnabled: boolean;
}) {
    return actionsVisible && isBuilder && canAdd && !isGeneralJob && isGlobalSpecCatalogEnabled;
}

/**
 * Shared "Save to catalog" mutation for all specification surfaces. Copies a job-level spec up to
 * the company catalog as a new, independent spec, then shows a success/error toast. Guards against
 * concurrent duplicate submissions for the same spec id (e.g. a double-click on a grid row action)
 * so a single spec can't be copied to the catalog twice.
 */
export function useSaveSpecificationToCatalog() {
    const { mutateAsync } = useSpecificationsSaveToCatalogByIdPostV2();
    const inFlightIds = useRef(new Set<number>());

    const saveToCatalog = useCallback(
        async ({ id, jobId }: EntityIdJobIdResponse): Promise<boolean> => {
            // Ignore a repeat submit while the first request for this spec is still in flight.
            if (inFlightIds.current.has(id)) {
                return false;
            }
            inFlightIds.current.add(id);
            try {
                const response = await mutateAsync({ id, params: { jobId } });
                void message.success(`"${response.data.title}" saved to catalog`);
                return true;
            } catch (error) {
                // showApixErrorMessage reads only `.message`; an AxiosError satisfies that shape.
                showApixErrorMessage(error as Error, "Failed to save specification to catalog");
                return false;
            } finally {
                inFlightIds.current.delete(id);
            }
        },
        [mutateAsync],
    );

    return { saveToCatalog };
}

/**
 * Bulk "Save to catalog" mutation for the specification list FAB. Copies multiple job-level specs up
 * to the company catalog in one request; the backend saves the whole set in a single transaction
 * (all-or-nothing) so it either saves every spec or throws without saving any. Returns the number
 * saved (the full count on success) for the shared BulkAction UI. All selected specs share a single
 * job (the list is scoped to one job), so the jobId is taken from the first entry.
 */
export function useBulkSaveSpecificationsToCatalog() {
    const { mutateAsync } = useSpecificationsSaveToCatalogPostV2();

    const saveBatchToCatalog = useCallback(
        async (batch: EntityIdJobIdResponse[]): Promise<number> => {
            if (batch.length === 0) {
                return 0;
            }
            const response = await mutateAsync({
                data: { jobSpecificationIdsList: batch.map(({ id, jobId }) => ({ id, jobId })) },
                params: { jobId: batch[0].jobId },
            });
            return response.data.successCount;
        },
        [mutateAsync],
    );

    return { saveBatchToCatalog };
}

/**
 * Fetches which of the given specs have unpublished changes (published specs whose live content has
 * drifted from the last-published snapshot). Shared by every "Save to catalog" surface so the
 * confirmation prompt can explain that the *published* version will be saved and the draft is left
 * untouched. Returns the flagged subset (empty when none drift), each with its published/draft dates.
 */
export function useUnpublishedChangesInfo() {
    const { mutateAsync } = useSpecificationsUnpublishedChangesInfoPostV2();

    const getUnpublishedInfo = useCallback(
        async (specs: EntityIdJobIdResponse[]): Promise<SpecificationUnpublishedInfo[]> => {
            if (specs.length === 0) {
                return [];
            }
            const response = await mutateAsync({
                data: { jobSpecificationIdsList: specs.map(({ id, jobId }) => ({ id, jobId })) },
                params: { jobId: specs[0].jobId },
            });
            return response.data.specificationsWithUnpublishedChanges;
        },
        [mutateAsync],
    );

    return { getUnpublishedInfo };
}

/**
 * Single-spec "Save to catalog" with the unpublished-changes confirmation. Looks up whether the spec
 * has unpublished changes; if so, prompts that the currently *published* version will be saved (the
 * private draft stays untouched) before saving. Specs with no unpublished changes save immediately
 * with no prompt. Shared by the Book viewer, the AG Grid detail modal, and the grid row-actions menu.
 */
export function useSaveSpecificationToCatalogWithConfirm() {
    const { saveToCatalog } = useSaveSpecificationToCatalog();
    const { getUnpublishedInfo } = useUnpublishedChangesInfo();
    const formatDate = useFormatDate();

    const saveToCatalogWithConfirm = useCallback(
        async (spec: EntityIdJobIdResponse): Promise<boolean> => {
            let unpublished: SpecificationUnpublishedInfo[];
            try {
                unpublished = await getUnpublishedInfo([spec]);
            } catch (error) {
                showApixErrorMessage(error as Error, "Failed to save specification to catalog");
                return false;
            }

            const info = unpublished.find((u) => u.id === spec.id && u.jobId === spec.jobId);
            if (info) {
                const result = await btConfirmAsync({
                    title: "Unpublished changes",
                    okText: "Save",
                    cancelText: "Cancel",
                    closable: true,
                    content: `This spec has unpublished draft changes. Saving now adds the currently published version (published ${formatDate(
                        info.publishedDate,
                        { showDate: true, showTime: false, showDayOfWeek: false },
                    )}) to the catalog.`,
                });
                if (result !== "ok") {
                    return false;
                }
            }

            return await saveToCatalog(spec);
        },
        [getUnpublishedInfo, saveToCatalog, formatDate],
    );

    return { saveToCatalogWithConfirm };
}
